import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';
import { showErrorToast } from '../../utils/toast';
import { formatDate } from '../../utils/helper';

const initialState = {
  users: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  },
  loading: false,
  error: null,
  filters: {
    search: '',
    type: 'default',
    page: 1,
    limit: 10,

    startDate: null,
    endDate: null,
  },
};

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().users;
      
      // Create a new URLSearchParams instance with all filter values
      const queryParams = new URLSearchParams();
      
      // Always include pagination parameters
      queryParams.set('page', String(filters.page));
      queryParams.set('limit', String(filters.limit));
      
      // Add search parameters if they exist
      if (filters.search) {
        queryParams.set('search', filters.search);
        if (filters.type !== 'default') {
          queryParams.set('type', filters.type);
        }
      }


      // Add date range if both dates are present
      if (filters.startDate && filters.endDate) {
        queryParams.set('startDate', formatDate(filters.startDate));
        queryParams.set('endDate', formatDate(filters.endDate));
      }

      // Update URL with current filters
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      window.history.pushState({}, '', newUrl);

      const response = await axiosInstance.get(`/user/allUsers?${queryParams}`);
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch users';
      showErrorToast(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setFilter: (state, action) => {
      // Create a new filters object with the updates
      const newFilters = { ...state.filters, ...action.payload };
      
      // Reset page to 1 if any filter other than page changes
      if (Object.keys(action.payload).some(key => key !== 'page')) {
        newFilters.page = 1;
      }
      
      state.filters = newFilters;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      
      // Update URL when filters are reset
      const queryParams = new URLSearchParams({
        page: String(initialState.filters.page),
        limit: String(initialState.filters.limit),
      });
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      window.history.pushState({}, '', newUrl);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilter, resetFilters } = usersSlice.actions;
export default usersSlice.reducer;